import type { EchoflicksSessionEvent } from "../types";

type JsonRecord = Record<string, unknown>;

export type PetAdaptationDog = {
  identityRef: string;
  datasetImages?: number;
  trainingState?: string;
  adapterState?: string;
  trainerSeconds?: number;
  totalSeconds?: number;
  costUsd?: string;
  boundScenes: number;
  qcAccepted: number;
  qcRejected: number;
  fallbackScenes: number;
  failedScenes: number;
  lastReason?: string;
};

export type PetNeutralExplanation = {
  reason: string;
  stage: string;
  occurredAt?: string;
  downstreamReasons: string[];
};

export type PetAdaptationAudit = {
  applicable: boolean;
  consentStatus?: string;
  policyMode?: string;
  lifecycleState?: string;
  adapterSetState?: string;
  deletionState?: string;
  totalCostUsd?: string;
  runpodCostUsd?: string;
  eligibleScenes: number;
  adaptedScenes: number;
  fallbackScenes: number;
  failedScenes: number;
  observationCount: number;
  whyNeutral?: PetNeutralExplanation;
  dogs: PetAdaptationDog[];
  updatedAt?: string;
};

const EVENT_PREFIXES = ["pet_adaptation.", "pet_identity."] as const;
const SAFE_STATE = /^[a-z][a-z0-9_]{0,63}$/;
const SAFE_ID = /^eid_[A-Za-z0-9_-]{8,120}$/;
const DECISIVE_NEUTRAL_REASONS = new Set([
  "policy_off",
  "scope_denied",
  "offer_ineligible",
  "consent_declined",
  "receipt_missing",
  "identity_manifest_unavailable",
  "dataset_ineligible",
  "training_abstained",
  "training_failed",
  "validation_refused",
  "publication_unavailable",
  "adapter_wait_deadline",
  "resolver_failure",
  "scene_ineligible",
  "batch_partial",
  "batch_failed",
  "batch_wall_timeout",
  "authorization_revoked",
  "tombstoned",
  "composer_wait_timeout",
  "ordinary_route_fallback",
  "signed_cost_bound_exceeded",
  "signed_wall_bound_exceeded",
  "pod_cleanup_failed",
  "delivery_failed",
]);

const record = (value: unknown): JsonRecord =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as JsonRecord)
    : {};

const safeText = (value: unknown, max = 100): string | undefined => {
  if (typeof value !== "string") return undefined;
  const normalized = value.trim();
  return normalized && normalized.length <= max ? normalized : undefined;
};

const safeState = (value: unknown): string | undefined => {
  const text = safeText(value, 64);
  return text && SAFE_STATE.test(text) ? text : undefined;
};

const count = (value: unknown): number | undefined =>
  typeof value === "number" &&
  Number.isSafeInteger(value) &&
  value >= 0 &&
  value <= 10_000
    ? value
    : undefined;

const micros = (value: unknown): number | undefined => {
  return typeof value === "number" &&
    Number.isSafeInteger(value) &&
    value >= 0 &&
    value <= 1_000_000_000
    ? value
    : undefined;
};

const seconds = (value: unknown): number | undefined =>
  typeof value === "number" &&
  Number.isFinite(value) &&
  value >= 0 &&
  value <= 86_400
    ? Math.round(value * 100) / 100
    : undefined;

const money = (value: unknown): string | undefined => {
  const text =
    typeof value === "number" ? value.toFixed(6) : safeText(value, 30);
  return text && /^\d{1,8}(?:\.\d{1,6})?$/.test(text) ? text : undefined;
};

const dogIdentity = (payload: JsonRecord): string | undefined => {
  const value = safeText(
    payload.echo_identity_ref ?? payload.dog_identity_ref ?? payload.dog_id,
    124,
  );
  return value && SAFE_ID.test(value) ? value : undefined;
};

const eventPayload = (event: EchoflicksSessionEvent): JsonRecord => {
  const raw = record(event.raw_payload);
  return Object.keys(record(raw.payload)).length ? record(raw.payload) : raw;
};

export function buildPetAdaptationAudit(
  events: EchoflicksSessionEvent[],
): PetAdaptationAudit | null {
  const relevant = events
    .filter((event) =>
      EVENT_PREFIXES.some((prefix) => event.event_type.startsWith(prefix)),
    )
    .sort((left, right) => {
      const timeOrder = String(left.occurred_at ?? "").localeCompare(
        String(right.occurred_at ?? ""),
      );
      if (timeOrder) return timeOrder;
      if (left.event_type === "pet_adaptation.deletion_state") return -1;
      if (right.event_type === "pet_adaptation.deletion_state") return 1;
      return String(left.source_id).localeCompare(String(right.source_id));
    });
  if (!relevant.length) return null;

  const audit: PetAdaptationAudit = {
    applicable: true,
    eligibleScenes: 0,
    adaptedScenes: 0,
    fallbackScenes: 0,
    failedScenes: 0,
    observationCount: 0,
    dogs: [],
  };
  const dogs = new Map<string, PetAdaptationDog>();
  const seen = new Set<string>();
  let runpodCostMicros = 0;
  let tombstoned = false;
  const ensureDog = (identityRef: string): PetAdaptationDog => {
    const existing = dogs.get(identityRef);
    if (existing) return existing;
    const created: PetAdaptationDog = {
      identityRef,
      boundScenes: 0,
      qcAccepted: 0,
      qcRejected: 0,
      fallbackScenes: 0,
      failedScenes: 0,
    };
    dogs.set(identityRef, created);
    return created;
  };

  for (const event of relevant) {
    const payload = eventPayload(event);
    const observationId = safeText(payload.observation_id, 64);
    const dedupeKey = observationId ?? safeText(event.source_id, 200);
    if (dedupeKey && seen.has(dedupeKey)) continue;
    if (dedupeKey) seen.add(dedupeKey);
    if (tombstoned && event.event_type !== "pet_adaptation.deletion_state") {
      continue;
    }
    audit.updatedAt = safeText(event.occurred_at, 40) ?? audit.updatedAt;
    const identityRef = dogIdentity(payload);
    const dog = identityRef ? ensureDog(identityRef) : undefined;

    const explainNeutral = (reason: string | undefined, stage: string) => {
      if (!reason || !DECISIVE_NEUTRAL_REASONS.has(reason)) return;
      if (!audit.whyNeutral) {
        audit.whyNeutral = {
          reason,
          stage,
          occurredAt: safeText(event.occurred_at, 40),
          downstreamReasons: [],
        };
      } else if (
        reason !== audit.whyNeutral.reason &&
        !audit.whyNeutral.downstreamReasons.includes(reason)
      ) {
        audit.whyNeutral.downstreamReasons.push(reason);
      }
    };

    switch (event.event_type) {
      case "pet_adaptation.consent_decided":
        audit.consentStatus = safeState(payload.status);
        audit.policyMode = safeState(payload.policy_mode ?? payload.mode);
        audit.lifecycleState =
          audit.consentStatus === "accepted" ? "authorized" : "declined";
        explainNeutral(
          audit.consentStatus === "declined" ? "consent_declined" : undefined,
          "consent",
        );
        break;
      case "pet_adaptation.dataset_planned":
        audit.lifecycleState = "dataset_ready";
        if (dog) {
          dog.datasetImages = count(payload.selected_image_count);
          dog.lastReason = safeState(payload.reason);
        }
        break;
      case "pet_adaptation.training_state":
        audit.lifecycleState = safeState(payload.state) ?? audit.lifecycleState;
        if (dog) {
          dog.trainingState = safeState(payload.state);
          dog.trainerSeconds = seconds(payload.trainer_seconds);
          dog.totalSeconds = seconds(payload.total_seconds);
          dog.costUsd = money(payload.cost_usd);
          dog.lastReason = safeState(payload.reason ?? payload.failure_code);
          explainNeutral(
            dog.trainingState === "failed"
              ? "training_failed"
              : dog.trainingState === "abstained"
                ? "training_abstained"
                : undefined,
            "training",
          );
        }
        break;
      case "pet_adaptation.adapter_set_frozen":
        audit.adapterSetState = safeState(payload.state);
        audit.lifecycleState = "adapter_set_frozen";
        break;
      case "pet_identity.scene_binding":
        if (dog) {
          const action = safeState(payload.action);
          if (action === "identity_adapter") dog.boundScenes += 1;
          if (action?.includes("fallback") || action === "source_only_ltx") {
            dog.fallbackScenes += 1;
            audit.fallbackScenes += 1;
            explainNeutral("ordinary_route_fallback", "scene");
          }
          dog.lastReason = safeState(payload.reason);
        }
        break;
      case "pet_identity.qc.observed":
        if (dog) {
          if (payload.passed === true) {
            dog.qcAccepted += 1;
          } else {
            dog.qcRejected += 1;
          }
          if (safeState(payload.disposition)?.includes("fallback"))
            dog.fallbackScenes += 1;
        }
        break;
      case "pet_adaptation.cost_reported":
        audit.totalCostUsd = money(payload.cost_usd);
        break;
      case "pet_adaptation.deletion_state":
        audit.deletionState = safeState(payload.state);
        audit.lifecycleState = audit.deletionState;
        tombstoned = true;
        explainNeutral("tombstoned", "deletion");
        break;
      case "pet_adaptation.observation": {
        audit.observationCount += 1;
        const stage = safeState(payload.stage) ?? "unknown";
        const state = safeState(payload.state);
        const reason = safeState(payload.reason_code);
        const detail = safeState(payload.detail_reason_code);
        audit.lifecycleState = state ?? audit.lifecycleState;
        const eligible =
          stage === "composer_wait" && state === "started"
            ? (count(payload.eligible_scene_count) ?? 0)
            : 0;
        const adapted =
          stage === "scene" && state === "adapted"
            ? (count(payload.adapted_scene_count) ?? 0)
            : 0;
        const fallback =
          stage === "fallback" && safeText(payload.scene_id, 160)
            ? (count(payload.fallback_scene_count) ?? 0)
            : 0;
        const failed =
          stage === "batch" ? (count(payload.failed_scene_count) ?? 0) : 0;
        audit.eligibleScenes += eligible;
        audit.adaptedScenes += adapted;
        audit.fallbackScenes += fallback;
        audit.failedScenes += failed;
        if (stage === "batch") {
          runpodCostMicros += micros(payload.estimated_cost_usd_micros) ?? 0;
        }
        if (dog) {
          dog.boundScenes += adapted;
          dog.fallbackScenes += fallback;
          dog.failedScenes += failed;
          dog.trainingState =
            stage === "training"
              ? (state ?? dog.trainingState)
              : dog.trainingState;
          dog.lastReason = reason ?? detail ?? dog.lastReason;
        }
        if (stage === "deletion" && state === "deleted") {
          audit.deletionState = "deleted";
          audit.lifecycleState = "deleted";
          tombstoned = true;
        }
        explainNeutral(reason, stage);
        if (
          detail &&
          audit.whyNeutral &&
          detail !== audit.whyNeutral.reason &&
          !audit.whyNeutral.downstreamReasons.includes(detail)
        ) {
          audit.whyNeutral.downstreamReasons.push(detail);
        }
        break;
      }
      default:
        break;
    }
    if (dog)
      dog.adapterState = safeState(payload.adapter_state) ?? dog.adapterState;
  }

  audit.dogs = [...dogs.values()].sort((left, right) =>
    left.identityRef.localeCompare(right.identityRef),
  );
  if (runpodCostMicros > 0) {
    audit.runpodCostUsd = (runpodCostMicros / 1_000_000).toFixed(6);
  }
  return audit;
}
